import {
    getDefaultMiddleware,
    createSlice,
} from "@reduxjs/toolkit"

import { Middleware } from "redux" // eslint-disable-line
import { Slice, PayloadAction } from "@reduxjs/toolkit" // eslint-disable-line
import { IBreadcrumbItem, ICommandBarItemProps, MessageBarType } from 'office-ui-fabric-react' // eslint-disable-line
import React, { ReactElement } from 'react' // eslint-disable-line

/**
 * Common middlewares, must have "getDefaultMiddleware"
 * @type {Middleware[]}
 */
const commonMiddleware = [
    ...getDefaultMiddleware()
]

/**
 * Payload Init
 * @typedef {object} PayloadMessageBar
 * @property {boolean} isDisplayed Is message bar display ?
 * @property {MessageBarType=} type MessageBarType 
 * @property {(string|object)=} message Message to show
 * 
 * Payload Url
 * @typedef {string} PayloadUrl Current Url
 * 
 * Payload Breadcrumb
 * @typedef {IBreadcrumbItem[]=} PayloadBreadcrumb Breadcrumb items
 * 
 * Payload Command
 * @typedef {ICommandBarItemProps[]=} PayloadCommand Commanbar items
 * 
 * Payload Modal
 * @typedef {object} PayloadModal
 * @property {boolean} show Is modal displayed
 * @property {string=} title Title
 * @property {string=} subTitle Subtitle
 * @property {function=} callback Callback when clicking "Yes" button
 * @property {ReactElement=} content JSX content
 */

/**
 * Common State
 * @typedef {object} CommonState
 * @property {string} selectedKeyURL Current URL in application
 * @property {object} messageBar Messagebar
 * @property {boolean} messageBar.isDisplayed Is message bar displayed
 * @property {MessageBarType=} messageBar.type Messagebar type
 * @property {string=} messageBar.message Text
 * @property {IBreadcrumbItem[]} breadcrumb Breadcrumb items
 * @property {ICommandBarItemProps[] | object[]} command Command items
 * @property {object} modal Modal
 * @property {boolean} modal.show Is modal displayed
 * @property {string=} modal.title Title
 * @property {string=} modal.subTitle Subtitle
 * @property {function=} modal.callback Callback when clicking "Yes" button
 * @property {ReactElement=} modal.content JSX content
*/
/**
 * Common Slice
 * @type {Slice<CommonState>}
 */
const commonSlice = createSlice({
    name: "common",
    /** @type {CommonState} */
    initialState: {
        selectedKeyURL: '/',
        messageBar: {
            isDisplayed: false,
            type: null,
            message: null
        },
        breadcrumb: [],
        command: [],
        modal: {
            show: false,
            title: '',
            subTitle: '',
            callback: () => null,
            content: <></>
        }
    },
    reducers: {
        /**
         * Set MessageBar
         * @param {PayloadAction<PayloadMessageBar>} action
         */
        setMessageBar: (state, action) => {
            switch (action.payload.type) {
                case MessageBarType.error:
                    action.payload.message = action.payload.message ? (typeof action.payload.message !== 'object' ? action.payload.message : JSON.stringify(action.payload.message)) : "Une erreur est survenue"
                    break
                case MessageBarType.blocked:
                    action.payload.message = action.payload.message ? (typeof action.payload.message !== 'object' ? action.payload.message : JSON.stringify(action.payload.message)) : "Vous n'êtes pas authorisé à faire ceci"
                    break
                case MessageBarType.success:
                    action.payload.message = action.payload.message ? (typeof action.payload.message !== 'object' ? action.payload.message : JSON.stringify(action.payload.message)) : "L'opération s'est correctement déroulée."
                    break
                default:
                    break
            }
            state.messageBar = action.payload
        },
        /**
         * Set Current Url
         * @param {PayloadAction<PayloadUrl>} action
         */
        setUrl: (state, action) => {
            state.selectedKeyURL = action.payload
        },
        /**
         * Set Breadcrumb
         * @param {PayloadAction<PayloadBreadcrumb>} action
         */
        setBreadcrumb: (state, action) => {
            state.breadcrumb = action.payload
        },
        /**
         * Set CommandBar
         * @param {PayloadAction<PayloadCommand>} action
         */
        setCommand: (state, action) => {
            state.command = action.payload
        },
        /**
         * Set Modal
         * @param {PayloadAction<PayloadModal>} action
         */
        setModal: (state, action) => {
            state.modal = action.payload
        },
    },
})

const { setMessageBar, setUrl, setBreadcrumb, setCommand, setModal } = commonSlice.actions
const commonReducer = commonSlice.reducer

export {
    setMessageBar, setUrl, setBreadcrumb, setCommand, setModal, //Reducers, used to call actions
    commonReducer, //All reducers, used to create store
    commonMiddleware //Middleware
}